import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWebSocket } from '../../hooks/useWebSocket';
import { getPublicWsOrigin } from '../../config/env';
import { AudioRecorder } from './AudioRecorder';
import { QuestionDisplay } from './QuestionDisplay';
import { TranscriptView } from './TranscriptView';
import { ProctoringMonitor } from '../proctoring/ProctoringMonitor';
import './InterviewRoom.css';

const InterviewRoom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [interviewStatus, setInterviewStatus] = useState('loading');
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [isRecording, setIsRecording] = useState(false);

  const token = localStorage.getItem('token');
  const wsUrl = `${getPublicWsOrigin()}/ws/interview/${id}?token=${token}`;
  
  const { socket, connected } = useWebSocket(wsUrl);

  useEffect(() => {
    if (socket) {
      socket.on('connected', (data) => {
        console.log('✅ Connected to interview:', data);
        setInterviewStatus('ready');
        // Interview auto-starts on backend, no need to emit start_interview
      });
      
      socket.on('error', (data) => {
        console.error('❌ Interview error:', data);
        alert(`Error: ${data.message || 'Unknown error'}`);
        setInterviewStatus('error');
      });

      socket.on('question', (data) => {
        console.log('📝 Question received:', data);
        setCurrentQuestion(data.data || data);
        setIsRecording(false);
        setTranscript('');
        setEvaluation(null);
        setInterviewStatus('ready');
      });

      socket.on('transcription', (data) => {
        setTranscript(data.text);
      });

      socket.on('evaluation', (data) => {
        setEvaluation(data.data);
      });

      socket.on('next_question', (data) => {
        console.log('📝 Next question received:', data);
        setCurrentQuestion(data.data || data);
        setIsRecording(false);
        setTranscript('');
        setEvaluation(null);
      });

      socket.on('interview_completed', (data) => {
        setInterviewStatus('completed');
        setTimeout(() => {
          navigate(`/dashboard/interview/${id}/report`);
        }, 3000);
      });

      socket.on('warning', (data) => {
        alert(`Warning: ${data.message}`);
      });

      socket.on('error', (data) => {
        console.error('WebSocket error:', data);
        alert(`Error: ${data.message}`);
      });
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket, id, navigate]);

  const handleStartRecording = () => {
    setIsRecording(true);
  };

  const handleStopRecording = (chunks) => {
    setIsRecording(false);
    
    // Send audio chunks to server
    if (socket && chunks.length > 0) {
      chunks.forEach((chunk, index) => {
        socket.emit('audio_chunk', {
          type: 'audio_chunk',
          audio: chunk
        });
      });
      
      // Signal end of answer
      socket.emit('end_answer', { type: 'end_answer' });
    }
  };

  const handleCompleteInterview = () => {
    if (socket) {
      socket.emit('complete_interview', { type: 'complete_interview' });
    }
  };

  if (interviewStatus === 'loading') {
    return (
      <div className="interview-room loading">
        <div className="loading-spinner">Connecting to interview...</div>
      </div>
    );
  }

  if (interviewStatus === 'completed') {
    return (
      <div className="interview-room completed">
        <h2>Interview Completed!</h2>
        <p>Redirecting to results...</p>
      </div>
    );
  }

  return (
    <div className="interview-room">
      <div className="interview-header">
        <h2>AI Interview Session</h2>
        <div className="status-indicator">
          <span className={connected ? 'connected' : 'disconnected'}>
            {connected ? '● Connected' : '○ Disconnected'}
          </span>
        </div>
      </div>

      <div className="interview-content">
        <div className="main-section">
          <ProctoringMonitor interviewId={id} />

          {currentQuestion ? (
            <QuestionDisplay 
              question={currentQuestion}
              onQuestionComplete={() => setIsRecording(true)}
            />
          ) : (
            <div className="waiting-for-question">
              <p>Waiting for first question...</p>
              <p className="help-text">Make sure WebSocket is connected (check status indicator)</p>
            </div>
          )}

          <AudioRecorder
            isRecording={isRecording}
            onStart={handleStartRecording}
            onStop={handleStopRecording}
            disabled={!currentQuestion}
          />

          {transcript && (
            <TranscriptView 
              transcript={transcript}
              evaluation={evaluation}
            />
          )}
        </div>

        <div className="sidebar">
          <button
            className="btn btn-danger"
            onClick={handleCompleteInterview}
            disabled={interviewStatus === 'completed'}
          >
            End Interview
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewRoom;

